/**
 * Globo / Tipo FBT row enhancer — safe, idempotent, non-destructive.
 *
 * Transforms Tipo's rendered FBT widget (inside .productBoughtTogether)
 * to match our native .product-fbt design:
 *   image · title · price · qty-stepper · ATC-icon
 * while keeping Tipo's native "Add All to Cart" button untouched.
 *
 * Safety:
 *   - Never removes Tipo elements; only adds new ones and/or hides via CSS
 *   - Per-row data-fbt-enhanced flag → idempotent
 *   - try/catch around each row so a failure on one doesn't block the others
 *   - MutationObserver + 500ms poll for 30s to handle late/re-renders
 */
(function () {
  'use strict';

  var TAG = '[fbt-enh]';
  var ROOT_SEL     = '.productBoughtTogether';
  var ROW_LIST_SEL = '.ga-products-input';
  var IMG_LIST_SEL = '.ga-products_image';
  var ROW_SEL      = 'li.ga-product[data-product-id]';
  var IMG_CELL_SEL = '.ga-product[data-product-id]';
  var FLAG         = 'data-fbt-enhanced';

  var CART_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="20" height="20" fill="currentColor" aria-hidden="true">' +
    '<path d="M230.14,58.87A8,8,0,0,0,224,56H62.68L56.6,22.57A8,8,0,0,0,48.73,16H24a8,8,0,0,0,0,16h18L67.56,172.29a24,24,0,0,0,5.33,11.27,28,28,0,1,0,44.4,8.44h45.42A27.75,27.75,0,0,0,160,204a28,28,0,1,0,28-28H91.17a8,8,0,0,1-7.87-6.57L80.13,152h116a24,24,0,0,0,23.61-19.71l12.16-66.86A8,8,0,0,0,230.14,58.87ZM104,204a12,12,0,1,1-12-12A12,12,0,0,1,104,204Zm96,0a12,12,0,1,1-12-12A12,12,0,0,1,200,204Zm4-74.57A8,8,0,0,1,196.1,136H77.22L65.59,72H214.41Z"/>' +
    '</svg>';

  function safeLog() {
    if (typeof console === 'undefined' || !console.log) return;
    var a = [TAG].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, a);
  }

  function buildImageMap(imgList) {
    var map = {};
    if (!imgList) return map;
    /* Tipo's top-strip image cells don't have data-product-id — they use
       id="ga-product_<productId>" or data-product-id OR rely on the <img>
       having class="ga-product_image_<productId>". Try all three. */
    imgList.querySelectorAll('.ga-product').forEach(function (cell) {
      var pid = cell.getAttribute('data-product-id');
      if (!pid) {
        var id = cell.getAttribute('id') || '';
        var m = id.match(/ga-product_(\d+)/);
        if (m) pid = m[1];
      }
      var img = cell.querySelector('img');
      if (!pid && img) {
        var cls = img.getAttribute('class') || '';
        var cm = cls.match(/ga-product_image_(\d+)/);
        if (cm) pid = cm[1];
      }
      if (pid && img && !map[pid]) map[pid] = img;
    });
    return map;
  }

  function injectImage(row, srcImg, href) {
    if (row.querySelector('.fbt-enh__image-link')) return;
    try {
      var link = document.createElement('a');
      link.href = href || '#';
      link.className = 'fbt-enh__image-link';
      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-hidden', 'true');
      var clone = srcImg.cloneNode(true);
      clone.className = 'fbt-enh__image';
      clone.removeAttribute('id');
      link.appendChild(clone);
      row.insertBefore(link, row.firstChild);
    } catch (e) { safeLog('image inject failed', e); }
  }

  function injectQty(row) {
    if (row.querySelector('.fbt-enh__qty')) return null;
    try {
      var wrap = document.createElement('div');
      wrap.className = 'fbt-enh__qty';
      wrap.innerHTML =
        '<button type="button" class="fbt-enh__qty-btn" data-step="-1" aria-label="Decrease quantity">&minus;</button>' +
        '<input type="number" class="fbt-enh__qty-input" value="1" min="1" aria-label="Quantity">' +
        '<button type="button" class="fbt-enh__qty-btn" data-step="1"  aria-label="Increase quantity">+</button>';
      row.appendChild(wrap);
      var input = wrap.querySelector('.fbt-enh__qty-input');
      wrap.querySelectorAll('.fbt-enh__qty-btn').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var step = parseInt(btn.getAttribute('data-step'), 10) || 0;
          var cur = parseInt(input.value, 10) || 1;
          input.value = Math.max(1, cur + step);
          recalcTotal(row.closest(ROOT_SEL));
        });
      });
      input.addEventListener('change', function () {
        var v = parseInt(input.value, 10);
        input.value = (isNaN(v) || v < 1) ? 1 : v;
        recalcTotal(row.closest(ROOT_SEL));
      });
      input.addEventListener('input', function () {
        recalcTotal(row.closest(ROOT_SEL));
      });
      /* Also recalc when Tipo's per-row checkbox toggles selection.
         Hidden visually in our overlay but still drives Tipo's count. */
      var cb = row.querySelector('input.selectedItem');
      if (cb && !cb.__fbtRecalcBound) {
        cb.__fbtRecalcBound = true;
        cb.addEventListener('change', function () {
          recalcTotal(row.closest(ROOT_SEL));
        });
      }
      /* Variant change (when the variant selector is enabled) updates the
         row's unit price, so refresh the total. */
      var vSel = row.querySelector('.ga-product_variant_select');
      if (vSel && !vSel.__fbtRecalcBound) {
        vSel.__fbtRecalcBound = true;
        vSel.addEventListener('change', function () {
          recalcTotal(row.closest(ROOT_SEL));
        });
      }
      return input;
    } catch (e) { safeLog('qty inject failed', e); return null; }
  }

  /* ── Total recalculation ──────────────────────────────────────
     Tipo's template_2 widget displays a "Total price: $X" in
     .ga-product_totalprice, computed server-side once at render and
     then never updated. Our enhancer adds per-row qty steppers, so
     the total goes stale the moment a user touches the stepper or
     unchecks a row. This sums (row.qty × row.variant_price) and
     refreshes the existing total span — without replacing any of
     Tipo's own DOM beyond that one text node.

     Price source: each row's <select> options carry data-price
     (cents) — we read from the currently-selected option, falling
     back to the first option to avoid 0.
     -------------------------------------------------------------- */
  function fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function getRowVariantPrice(row) {
    var sel = row.querySelector('.ga-product_variant_select');
    if (!sel) return 0;
    var opt = sel.options[sel.selectedIndex] || sel.options[0];
    return opt ? (parseInt(opt.getAttribute('data-price'), 10) || 0) : 0;
  }
  function recalcTotal(root) {
    if (!root) return;
    var totalEl = root.querySelector('.ga-product_totalprice');
    if (!totalEl) return;
    var total = 0;
    root.querySelectorAll(ROW_SEL).forEach(function (row) {
      var cb = row.querySelector('input.selectedItem');
      if (cb && cb.checked === false) return;
      var qtyInput = row.querySelector('.fbt-enh__qty-input');
      var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      total += getRowVariantPrice(row) * qty;
    });
    totalEl.textContent = fmtMoney(total);
  }

  function injectAtc(row, qtyInput) {
    if (row.querySelector('.fbt-enh__atc')) return;
    try {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fbt-enh__atc';
      btn.setAttribute('aria-label', 'Add to cart');
      btn.innerHTML = CART_ICON;
      row.appendChild(btn);

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (btn.classList.contains('is-loading')) return;

        var sel = row.querySelector('.ga-product_variant_select');
        var vid = null;
        if (sel && sel.value) vid = sel.value;
        else if (sel) {
          var first = sel.querySelector('option:not([disabled])');
          if (first) vid = first.value;
        }
        if (!vid) {
          var cb = row.querySelector('input.selectedItem');
          if (cb && cb.value) vid = cb.value;
        }
        if (!vid) { safeLog('no variant id'); return; }

        /* Always read qty fresh from the row — not the captured closure
           reference — so a later re-render or stale binding can't pin it to 1. */
        var freshQty = row.querySelector('.fbt-enh__qty-input');
        var qty = freshQty ? Math.max(1, parseInt(freshQty.value, 10) || 1) : 1;

        btn.classList.add('is-loading');
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: parseInt(vid, 10), quantity: qty })
        })
        .then(function (r) { return r.ok ? r.json() : r.text().then(function (t) { throw new Error(t); }); })
        .then(function () {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          try {
            var ct = document.body.dataset.cartType || 'page';
            if (ct === 'drawer' || ct === 'hybrid') {
              window._cartFetchPromise = fetch('/cart.js').then(function (r) { return r.json(); });
              document.dispatchEvent(new CustomEvent('cart:open'));
            } else {
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }
          } catch (_) {}
          setTimeout(function () {
            btn.classList.remove('is-added');
            btn.disabled = false;
          }, 1500);
        })
        .catch(function (err) {
          safeLog('ATC failed', err && err.message);
          btn.classList.remove('is-loading');
          btn.disabled = false;
        });
      });
    } catch (e) { safeLog('atc inject failed', e); }
  }

  /* Intercept Tipo's bundle "Add All to Cart" button.
     Tipo's native handler adds items then dispatches globoRelatedProductsUpdateCart,
     and in the process re-renders / clears the widget — leaving an empty FBT.
     We do the add ourselves with a single /cart/add.js call (items array),
     dispatch Horizon's own cart:refresh / cart:open events, and prevent
     Tipo's handler from running so the widget stays intact. */
  function enhanceAddAll(root) {
    if (!root || root.getAttribute('data-fbt-addall-enhanced') === 'true') return;
    root.setAttribute('data-fbt-addall-enhanced', 'true');

    root.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('.ga-addalltocart');
      if (!btn) return;
      /* Block Tipo's own click handlers + any <form> submit the button may trigger. */
      e.preventDefault();
      e.stopImmediatePropagation();

      if (btn.classList.contains('is-loading')) return;

      var rowList = root.querySelector(ROW_LIST_SEL);
      var rows = rowList ? rowList.querySelectorAll(ROW_SEL) : [];
      var items = [];
      rows.forEach(function (row) {
        /* Respect Tipo's per-row checkbox if present — unchecked rows are excluded. */
        var cb = row.querySelector('input.selectedItem');
        if (cb && cb.checked === false) return;

        var sel = row.querySelector('.ga-product_variant_select');
        var vid = null;
        if (sel && sel.value) vid = sel.value;
        else if (sel) {
          var first = sel.querySelector('option:not([disabled])');
          if (first) vid = first.value;
        }
        if (!vid && cb && cb.value) vid = cb.value;
        if (!vid) return;

        var qtyInput = row.querySelector('.fbt-enh__qty-input');
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
        items.push({ id: parseInt(vid, 10), quantity: qty });
      });

      if (items.length === 0) { safeLog('add-all: nothing to add'); return; }

      btn.classList.add('is-loading');
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function (r) { return r.ok ? r.json() : r.text().then(function (t) { throw new Error(t); }); })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        try {
          var ct = (document.body && document.body.dataset && document.body.dataset.cartType) || 'page';
          if (ct === 'drawer' || ct === 'hybrid') {
            window._cartFetchPromise = fetch('/cart.js').then(function (r) { return r.json(); });
            document.dispatchEvent(new CustomEvent('cart:open'));
          } else {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        } catch (_) {}
        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
        }, 1500);
      })
      .catch(function (err) {
        safeLog('add-all failed', err && err.message);
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
    }, true); /* capture: true → runs before Tipo's bubble-phase handlers */
  }

  function enhanceRow(row, imgMap) {
    if (!row || row.getAttribute(FLAG) === 'true') return;
    try {
      var pid = row.getAttribute('data-product-id');
      var titleLink = row.querySelector('.ga-product_title');
      var href = (titleLink && titleLink.getAttribute('href')) || '#';

      if (pid && imgMap[pid]) injectImage(row, imgMap[pid], href);
      var qtyInput = injectQty(row);
      injectAtc(row, qtyInput);

      row.setAttribute(FLAG, 'true');
    } catch (e) { safeLog('row enhance failed', e); }
  }

  /* Read block settings from the nearest .product-fbt--embed wrapper and
     apply them to Tipo's markup (heading + add-all button text). */
  function applyBlockSettings(root) {
    try {
      /* The wrapper that carries the data-* attrs sits just outside
         .productBoughtTogether — walk up to find it. */
      var wrapper = root.closest && root.closest('.product-fbt--embed');
      if (!wrapper) return;

      var customHeading = wrapper.getAttribute('data-fbt-heading');
      var addallText    = wrapper.getAttribute('data-fbt-addall-text');

      if (customHeading) {
        var title = root.querySelector('.ga-title');
        var span  = title && title.querySelector('span');
        var tgt   = span || title;
        if (tgt && tgt.textContent !== customHeading) {
          tgt.textContent = customHeading;
        }
      }

      if (addallText) {
        var btn = root.querySelector('.ga-addalltocart');
        if (btn) {
          /* Preserve Tipo's data-add / data-added labels for state changes */
          btn.setAttribute('data-add', addallText);
          if (btn.textContent.trim() !== addallText) btn.textContent = addallText;
        }
      }
    } catch (e) { safeLog('applyBlockSettings failed', e); }
  }

  function tryEnhance() {
    var root = document.querySelector(ROOT_SEL);
    if (!root) return false;
    var rowList = root.querySelector(ROW_LIST_SEL);
    if (!rowList) return false;
    var rows = rowList.querySelectorAll(ROW_SEL);
    if (rows.length === 0) return false;

    var imgList = root.querySelector(IMG_LIST_SEL);
    var imgMap = buildImageMap(imgList);

    var newlyEnhanced = 0;
    rows.forEach(function (row) {
      if (row.getAttribute(FLAG) !== 'true') {
        enhanceRow(row, imgMap);
        newlyEnhanced++;
      }
    });

    if (imgList && newlyEnhanced > 0) {
      imgList.classList.add('fbt-enh__images-hidden');
    }

    /* Apply settings-driven overrides every time (cheap; also reapplies
       after Tipo re-renders, so user-edited settings take effect). */
    applyBlockSettings(root);

    /* Install our capture-phase intercept on the root exactly once.
       Keeps Tipo from clearing / re-rendering the widget after Add All. */
    enhanceAddAll(root);

    if (newlyEnhanced > 0) safeLog('enhanced', newlyEnhanced, 'rows');
    return newlyEnhanced > 0 || rows.length > 0;
  }

  function init() {
    var root = document.querySelector(ROOT_SEL);
    if (!root) { safeLog('no .productBoughtTogether'); return; }
    safeLog('ready — watching');

    tryEnhance();

    var observer = new MutationObserver(function () { tryEnhance(); });
    observer.observe(root, { childList: true, subtree: true });

    /* Poll every 500ms for up to 30s as a safety net. */
    var started = Date.now();
    var interval = setInterval(function () {
      tryEnhance();
      if (Date.now() - started > 30000) clearInterval(interval);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
